import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { DemandOrderDto, IDemandOrderDto, SiteDto, OrderGoodsItemDto, DemandOrderDataDto } from '../../api/ClientApi';
import { RootState } from '../reducers';
import { myAssert } from '../../common/console';
import { WarehouseClient } from '../../api/Extensions';
import { filterUniqueBareGoodsByArticle } from './helper';

export const createNewEmptyDemandOrder = createAction('demandOrder/createNewEmptyDemandOrder');
export const clearDemandOrder = createAction('demandOrder/clearDemandOrder');

export const setDemandOrderDestinationSite = createAction<SiteDto | undefined>(
    'demandOrder/setDemandOrderDestinationSite',
);
export const removeDemandOrderDestinationSite = createAction('demandOrder/removeDemandOrderDestinationSite');
export const updateDemandOrderDescription = createAction<string | undefined>(
    'demandOrder/updateDemandOrderDescription',
);
export const updateDemandOrderData = createAction<string | undefined>('demandOrder/updateDemandOrderData');

export const createNewDemandOrder = createAction<IDemandOrderDto>('demandOrder/createNewDemandOrder');
export const addOrUpdateDemandOrder = createAction<DemandOrderDto>('demandOrder/addOrUpdateDemandOrder');

export const addOrderGoodsItemToDemandOrder = createAction<{
    demandOrderId: number;
    demandOrderGoodsItem?: OrderGoodsItemDto;
}>('demandOrder/addOrderGoodsItemToDemandOrder');

export const removeOrderGoodsItemFromDemandOrder = createAction<{
    demandOrderId: number;
    demandOrderGoodsItemId?: number;
}>('demandOrder/removeOrderGoodsItemFromDemandOrder');

export const saveDemandOrder = createAsyncThunk<DemandOrderDto, void>(
    'demandOrder/saveDemandOrder',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const demandOrderState = rootState.demandOrderForm;

        const demandOrder = demandOrderState.demandOrder;
        const bareGoods = rootState.goodsToOrderForm.bareGoodsEntities;

        myAssert(() => demandOrder !== null);

        if (demandOrder === null) {
            throw new Error();
        }

        const demandOrderId: number | undefined = demandOrder.id;
        let savedDemandOrder: DemandOrderDto | undefined;

        const newDemandOrder = new DemandOrderDto(demandOrder);

        const filteredBareGoods = filterUniqueBareGoodsByArticle(
            bareGoods ? bareGoods.filter((bg) => !!bg.article) : [],
        );

        if (!demandOrderId || demandOrderId <= 0) {
            const addDemandOrderPromise = WarehouseClient.addNewDemandOrder(newDemandOrder as DemandOrderDto).then(
                async (resDemandOrder) => {
                    let savedDemandOrderGoodsItems: OrderGoodsItemDto[] = [];
                    const savedDemandOrderGoodsItemsPromises: Promise<OrderGoodsItemDto>[] = [];
                    filteredBareGoods.map((bg) => {
                        const newDemandOrderGood = new OrderGoodsItemDto();
                        newDemandOrderGood.articleId = bg.article?.id;
                        newDemandOrderGood.article = bg.article;
                        newDemandOrderGood.demandOrder = resDemandOrder;
                        newDemandOrderGood.demandOrderId = resDemandOrder?.id;
                        newDemandOrderGood.quantity = bg.orderedQuantity;

                        const addDemandOrderGoodsItemPromise = WarehouseClient.addNewOrderGoodsItemForDemandOrder(
                            resDemandOrder?.id ?? 0,
                            newDemandOrderGood,
                        );

                        savedDemandOrderGoodsItemsPromises.push(addDemandOrderGoodsItemPromise);
                    });

                    savedDemandOrderGoodsItems = await Promise.all(savedDemandOrderGoodsItemsPromises);

                    resDemandOrder.orderGoodsItems = savedDemandOrderGoodsItems;

                    return resDemandOrder;
                },
            );

            savedDemandOrder = await addDemandOrderPromise;
        } else {
            myAssert(() => newDemandOrder.id !== undefined);

            if (newDemandOrder.id === undefined) {
                throw new Error();
            }

            const filteredDemandOrderGoodsItems = newDemandOrder.orderGoodsItems;

            let savedDemandOrderGoodsItems: OrderGoodsItemDto[] = [];
            let removedGoods: number[] = [];

            const savedDemandOrderGoodsItemsPromises: Promise<OrderGoodsItemDto>[] = [];
            const removedDemandOrderGoodsItemsIdsPromises: Promise<number>[] = [];

            // go through oldGoods;
            filteredDemandOrderGoodsItems?.map((fg) => {
                // check if this goods exists in new demandOrder
                const demandOrderedGoods = filteredBareGoods?.find((bg) => bg.article?.id === fg.article?.id);

                if (!!demandOrderedGoods) {
                    // if quantity is different, update existing goods quantity
                    if (demandOrderedGoods.orderedQuantity !== fg.quantity) {
                        fg.quantity = demandOrderedGoods.orderedQuantity;

                        const addDemandOrderGoodsItemPromise = WarehouseClient.addNewOrderGoodsItemForDemandOrder(
                            demandOrderId,
                            fg,
                        ).then((res) => {
                            thunkAPI.dispatch(
                                addOrderGoodsItemToDemandOrder({ demandOrderId, demandOrderGoodsItem: res }),
                            );
                            return res;
                        });

                        savedDemandOrderGoodsItemsPromises.push(addDemandOrderGoodsItemPromise);
                    }
                } else {
                    // this goods does not exist in new demandOrder, so we have to delete it and disconnect it from the demandOrder
                    const removedDemandOrderGoodsItemsIdsPromise = WarehouseClient.deleteOrderGoodsItem(
                        fg.id ?? 0,
                    ).then(() => {
                        thunkAPI.dispatch(
                            removeOrderGoodsItemFromDemandOrder({
                                demandOrderId: newDemandOrder.id ?? 0,
                                demandOrderGoodsItemId: fg.id,
                            }),
                        );
                        return fg.id ?? 0;
                    });
                    removedDemandOrderGoodsItemsIdsPromises.push(removedDemandOrderGoodsItemsIdsPromise);
                }
            });

            filteredBareGoods
                .filter((fbg) => !filteredDemandOrderGoodsItems?.find((fg) => fg.article?.id === fbg.article?.id))
                .map((fbg) => {
                    // in this case, goods does not exist in old demandOrder, so we have to add id and connect it to the demandOrder
                    const newDemandOrderGood = new OrderGoodsItemDto();
                    newDemandOrderGood.articleId = fbg.article?.id;
                    newDemandOrderGood.article = fbg.article;
                    newDemandOrderGood.demandOrder = savedDemandOrder;
                    newDemandOrderGood.demandOrderId = savedDemandOrder?.id;
                    newDemandOrderGood.quantity = fbg.orderedQuantity;

                    const addDemandOrderGoodsItemPromise = WarehouseClient.addNewOrderGoodsItemForDemandOrder(
                        demandOrderId,
                        newDemandOrderGood,
                    ).then((res) => {
                        thunkAPI.dispatch(addOrderGoodsItemToDemandOrder({ demandOrderId, demandOrderGoodsItem: res }));
                        return res;
                    });

                    savedDemandOrderGoodsItemsPromises.push(addDemandOrderGoodsItemPromise);
                });

            savedDemandOrderGoodsItems = await Promise.all(savedDemandOrderGoodsItemsPromises);

            removedGoods = await Promise.all(removedDemandOrderGoodsItemsIdsPromises);

            newDemandOrder.orderGoodsItems?.push(...savedDemandOrderGoodsItems);
            newDemandOrder.orderGoodsItems?.filter((ogi) => !removedGoods.includes(ogi.id ?? 0));

            const addDemandOrderPromise = WarehouseClient.getDemandOrder(demandOrderId);

            savedDemandOrder = await addDemandOrderPromise;
        }

        return savedDemandOrder;
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const demandOrderState = (thunkAPI.getState() as RootState).demandOrderForm;

            return demandOrderState.demandOrder !== null;
        },
    },
);

export const importDemandOrders = createAsyncThunk<DemandOrderDataDto>(
    'demandOrder/importDemandOrders',
    async (thunkAPI) => {
        return WarehouseClient.getAllDemandOrders();
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const demandOrdersState = (thunkAPI.getState() as RootState).demandOrders;

            return !demandOrdersState.demandOrdersFetchActive;
        },
    },
);

export const refreshDemandOrdersData = createAsyncThunk<void, void>(
    'demandOrder/refreshDemandOrdersData',
    async (payload: void, thunkAPI) => {
        const rootState = thunkAPI.getState() as RootState;
        const demandOrdersState = rootState.demandOrders;

        return WarehouseClient.getDemandOrdersDataRefreshInfo().then((info) => {
            if (info.infoDate && info.infoDate >= demandOrdersState!.demandOrdersInfoDate!) {
                thunkAPI.dispatch(importDemandOrders());
            }
        });
    },
    {
        condition: (payload: void, thunkAPI: { getState: any }) => {
            const demandOrdersState = (thunkAPI.getState() as RootState).demandOrders;

            if (demandOrdersState.demandOrdersInfoDate === null) {
                return false;
            }

            const demandOrderInfoDate: Date = new Date(demandOrdersState.demandOrdersInfoDate);
            demandOrderInfoDate.setMinutes(demandOrderInfoDate.getMinutes() + 1);

            return demandOrderInfoDate < new Date();
        },
    },
);

export const importDemandOrder = createAsyncThunk<DemandOrderDto, number>(
    'demandOrder/importDemandOrder',
    async (payload, thunkAPI) => {
        return WarehouseClient.getDemandOrder(payload);
    },
);

export const importDemandOrderForDemandOrderDetails = createAsyncThunk<DemandOrderDto, number>(
    'demandOrder/importDemandOrderForDemandOrderDetails',
    async (payload, thunkAPI) => {
        return WarehouseClient.getDemandOrder(payload);
    },
);

export const clearDemandOrderForDemandOrderDetails = createAction('demandOrder/clearDemandOrderForDemandOrderDetails');

export const updateDemandOrderForDemandOrderDetails = createAction<DemandOrderDto>(
    'demandOrder/updateDemandOrderForDemandOrderDetails',
);

export const removeDemandOrderForDemandOrderDetails = createAction(
    'demandOrder/removeDemandOrderForDemandOrderDetails',
);
